import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  BudgetDepartmentCandidate,
  BudgetDepartmentListItem,
  BudgetDepartmentRequest,
} from './budgetDepartmentTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  // Sent whenever the key is present, empty string included — "none of these" matches no row.
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const budgetDepartmentsApi = {
  clear: () => api<number>('/api/budget-departments/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<BudgetDepartmentListItem>>(`/api/budget-departments${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<BudgetDepartmentListItem>(`/api/budget-departments/${id}`, { signal }),

  /** Active units NOT already enrolled — offering one the server would refuse is a rejection waiting. */
  candidates: (signal?: AbortSignal) =>
    api<BudgetDepartmentCandidate[]>('/api/budget-departments/candidates', { signal }),

  /**
   * The enrolled departments, for a plan's roster.
   *
   * Gated on `budget-plans.read` server-side, not on this module's key: the roster is part of
   * reading a plan, and a reader of plans must not need a second grant to see who is expected
   * to propose.
   */
  roster: (signal?: AbortSignal) =>
    api<BudgetDepartmentListItem[]>('/api/budget-departments/roster', { signal }),

  create: (request: BudgetDepartmentRequest) =>
    api<BudgetDepartmentListItem>('/api/budget-departments', { method: 'POST', body: request }),

  /** Withdraws it from the roster. Its already-filed PPAs are left alone. */
  deactivate: (id: string) => api<void>(`/api/budget-departments/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/budget-departments/${id}/restore`, { method: 'POST' }),
};
